<template>
  <div
    class="truncate"
    :title="title"
  >
    <template
      v-if="!compactMode"
    >
      <p
        class="text-fg-base-fg22 text-label-r-13-18"
        :class="{
          'flex! items-center gap-x-1': showCircle,
          'mt-0!': !lastSeen,
          'font-light! mt-2!': forParticipantInChat && lastSeen }"
      >
        <template v-if="showOnlineStatus">
          <span
            v-if="showCircle"
            class="h-3 w-3 flex-none rounded-full bg-comp-avatar-status-online"
          />
          <p class="text-label-r-13-18">
            {{ $t('common.online') }}
          </p>
        </template>

        <template v-else-if="!isGuestPServed && statuses?.status === OwnStatus.ABSENT">
          <span
            v-if="showCircle"
            class="h-3 w-3 flex-none rounded-full bg-comp-avatar-status-do-not-disturb text-fg-base-fg22"
          />
          {{ $t('common.absent') }}
        </template>

        <template v-else-if="!isGuestPServed && statuses?.status === OwnStatus.DO_NOT_DISTURB">
          <span
            v-if="showCircle"
            class="h-3 w-3 flex-none rounded-full bg-comp-avatar-status-in-the-call"
          />
          {{ $t('common.dontDisturb') }}
        </template>

        <template v-else-if="showOfflineStatus">
          <div class="grid grid-cols-[1fr_auto] gap-1 items-center">
            <span
              v-if="showCircle"
              class="h-3 w-3 rounded-full border-2 border-comp-avatar-status-do-not-disturb bg-bg-base-bg1"
            />
            <span
              v-if="lastSeen"
              class="truncate"
            >
              {{ $t('common.wasOnlineIn', { lastSeen }) }}
            </span>
          </div>
        </template>
        <template v-if="statuses?.statusMessage && !withoutStatusMessage">
          <div
            v-if="!withoutStatusMessage"
            class="flex items-center"
            :title="statuses.statusMessage"
          >
            <span class="shrink-0 mr-1"> — </span>
            <span class="truncate">
              {{ statuses.statusMessage }}
            </span>
          </div>
        </template>
      </p>
    </template>

    <template v-else>
      <template v-if="showOnlineStatus">
        <span
          v-if="showCircle"
          data-test="contact-status-circle"
          class="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-comp-avatar-status-online"
        />
      </template>

      <template v-else-if="!isGuestPServed && statuses?.status === OwnStatus.ABSENT">
        <span
          v-if="showCircle"
          class="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-comp-avatar-status-do-not-disturb"
        />
      </template>

      <template v-else-if="!isGuestPServed && statuses?.status === OwnStatus.DO_NOT_DISTURB">
        <span
          v-if="showCircle"
          class="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-comp-avatar-status-in-the-call ring-1 ring-bg-base-bg1"
        />
      </template>

      <template v-else-if="showOfflineStatus">
        <span
          v-if="showCircle"
          class="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-comp-avatar-status-do-not-disturb bg-bg-base-bg1"
        />
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import 'dayjs/locale/en-gb'
import { PServed } from '@wui/im'
import dayjs, { unix } from 'dayjs'
import { computed, toRefs } from 'vue'

import { useLocalization } from '@/shared/composables/localization'
import { useStoreContactStatus } from '@/shared/contact-status'
import { useStoreGuestStatus } from '@/shared/guest-status'
import { OwnStatus } from '@/shared/types/store'
import { isGuest } from '@/shared/utils/uri'

const props = withDefaults(defineProps<{
  pServed: PServed,
  showCircle?: boolean,
  compactMode?: boolean,
  forParticipantInChat?: boolean
  withoutStatusMessage?: boolean
}>(), {
  showCircle: true,
  compactMode: false,
  forParticipantInChat: false,
  withoutStatusMessage: false,
})

const { pServed } = toRefs(props)

const { t, tc, currentLocale } = useLocalization()
const { getStatusByPServed: getContactStatusByPServed } = useStoreContactStatus()
const { getStatusByPServed: getGuestStatusByPServed } = useStoreGuestStatus()

const isGuestPServed = computed(() => isGuest(pServed.value))

const statuses = computed(() => {
  if (isGuestPServed.value) {
    return getGuestStatusByPServed(pServed.value)
  }
  return getContactStatusByPServed(pServed.value)
})

const showOnlineStatus = computed(() => {
  if (isGuestPServed.value) {
    return statuses.value?.isOnline === true
  }
  return statuses.value?.status === OwnStatus.ONLINE
})

const showOfflineStatus = computed(() => {
  if (isGuestPServed.value) {
    return statuses.value?.isOnline === false
  }
  return !!lastSeen.value
})

const lastSeen = computed(() => {
  if (!statuses.value) return undefined

  const lastSeen = statuses.value.lastSeen

  if (!lastSeen) return undefined

  if (dayjs().from(unix(lastSeen).tz(), true) === 'минута') {
    return tc('common.minutes', 1)
  }
  return dayjs().locale(currentLocale.value).from(unix(lastSeen).tz(), true)
})

const title = computed(() => {
  if (showOnlineStatus.value) {
    return t('common.online')
  }

  const status = statuses.value?.status
  if (status === OwnStatus.ABSENT) {
    return t('common.absent')
  } else if (status === OwnStatus.DO_NOT_DISTURB) {
    return t('common.dontDisturb')
  } else if (lastSeen.value) {
    return t('common.wasOnlineIn', { lastSeen: lastSeen.value })
  }

  return ''
})
</script>
